import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, Platform, StyleSheet } from 'react-native';
import { Paprika } from '../../../styles/theme';
import type { DietKind } from '../../../redux/mealPlanUISlice';
import { useTranslation } from 'react-i18next';
import { placeCellPopover, POPOVER_WIDTH, POPOVER_HEIGHT, type Rect } from './popoverPlacement';

interface Props {
  /** Day + meal of the cell being edited — the panel floats away from its cell. */
  title: string;
  /** Anchor cell, in window coordinates (used for hit-testing outside presses). */
  anchor: Rect;
  /** The overlay host this panel renders into, in window coordinates. */
  origin: Rect;
  /** Height at the bottom of the host covered by the floating action bar. */
  bottomInset: number;
  portions: number;
  kind: DietKind;
  defaultPortions: number;
  onPortionsChange: (n: number) => void;
  onKindChange: (k: DietKind) => void;
  onRemove: () => void;
  onClose: () => void;
}

const DIET_OPTIONS: { key: DietKind; labelKey: string }[] = [
  { key: 'auto',  labelKey: 'mealConfig.auto' },
  { key: 'fish',  labelKey: 'mealConfig.fish' },
  { key: 'veg',   labelKey: 'mealConfig.veggie' },
  { key: 'meat',  labelKey: 'mealConfig.meat' },
];

export function CellPopover({ title, anchor, origin, bottomInset, portions, kind, defaultPortions, onPortionsChange, onKindChange, onRemove, onClose }: Props) {
  const { t } = useTranslation();
  const panelRef = useRef<View | null>(null);

  // The panel's height depends on its content (the reset link only shows for an
  // overridden cell), so place with an estimate, then re-place once laid out —
  // otherwise a taller-than-estimated panel runs under the action bar.
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  const { left, top } = placeCellPopover(
    { x: anchor.x - origin.x, y: anchor.y - origin.y, width: anchor.width, height: anchor.height },
    { width: origin.width, height: origin.height },
    { width: POPOVER_WIDTH, height: measuredHeight ?? POPOVER_HEIGHT },
    bottomInset
  );

  /**
   * Dismissal without a backdrop. A full-screen Pressable would swallow the
   * click that dismissed it, so moving from one cell's panel to another would
   * cost two clicks. Listening on the document instead lets that same click
   * reach the next cell — but it must ignore presses inside the panel and
   * inside the anchor cell, whose own onPress already toggles the panel shut.
   */
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const onPointerDown = (e: PointerEvent) => {
      const node = panelRef.current as unknown as HTMLElement | null;
      if (node && e.target instanceof Node && node.contains(e.target)) return;
      const insideAnchor =
        e.clientX >= anchor.x && e.clientX <= anchor.x + anchor.width &&
        e.clientY >= anchor.y && e.clientY <= anchor.y + anchor.height;
      if (insideAnchor) return;
      onClose();
    };

    // The anchor is measured once, against the layout at press time — a resize
    // moves the cell out from under the panel, so close rather than drift.
    const onResize = () => onClose();

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor, onClose]);

  return (
    <>
      {/* Native has no document-level listener — fall back to a scrim there. */}
      {Platform.OS !== 'web' && (
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      )}

      <View
        ref={panelRef}
        style={[styles.popover, { left, top }]}
        onLayout={(e) => {
          const h = Math.round(e.nativeEvent.layout.height);
          setMeasuredHeight((prev) => (prev === h ? prev : h));
        }}
      >
        <Text style={styles.title}>{title}</Text>

        <Text style={styles.sectionLabel}>{t('thisWeek.portionsLabel')}</Text>
        <View style={styles.stepper}>
          <Pressable
            style={[styles.stepBtn, portions <= 1 && styles.stepBtnDisabled]}
            onPress={() => onPortionsChange(Math.max(1, portions - 1))}
          >
            <Text style={styles.stepBtnText}>−</Text>
          </Pressable>
          <Text style={styles.stepValue}>{portions}</Text>
          <Pressable
            style={[styles.stepBtn, portions >= 12 && styles.stepBtnDisabled]}
            onPress={() => onPortionsChange(Math.min(12, portions + 1))}
          >
            <Text style={styles.stepBtnText}>+</Text>
          </Pressable>
        </View>

        {portions !== defaultPortions && (
          <Pressable onPress={() => onPortionsChange(defaultPortions)}>
            <Text style={styles.resetLink}>{t('thisWeek.resetToWeekDefault', { count: defaultPortions })}</Text>
          </Pressable>
        )}

        <View style={styles.divider} />

        <Text style={styles.sectionLabel}>{t('mealConfig.mealType')}</Text>
        <View style={styles.dietGrid}>
          {DIET_OPTIONS.map((opt) => (
            <Pressable
              key={opt.key}
              style={[styles.dietOption, kind === opt.key && styles.dietOptionActive]}
              onPress={() => onKindChange(opt.key)}
            >
              <Text style={[styles.dietOptionText, kind === opt.key && styles.dietOptionTextActive]}>
                {t(opt.labelKey)}
              </Text>
            </Pressable>
          ))}
        </View>

        <View style={styles.divider} />

        <Pressable style={styles.removeBtn} onPress={onRemove}>
          <Text style={styles.removeBtnText}>{t('mealConfig.dontSuggestHere')}</Text>
        </Pressable>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  popover: {
    position: 'absolute',
    width: POPOVER_WIDTH,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 12,
    padding: 16,
    shadowColor: 'rgba(82, 68, 60, 0.12)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 16,
    zIndex: 70,
  } as any,
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 16,
    color: Paprika.textPrimary,
    marginBottom: 12,
  },
  sectionLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textMuted,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 8,
  },
  stepBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepBtnDisabled: {
    opacity: 0.35,
  },
  stepBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textPrimary,
    lineHeight: 20,
  },
  stepValue: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 28,
    color: Paprika.textPrimary,
    minWidth: 40,
    textAlign: 'center',
  },
  resetLink: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  divider: {
    height: 1,
    backgroundColor: Paprika.border,
    marginVertical: 12,
  },
  dietGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dietOption: {
    flex: 1,
    minWidth: '45%' as any,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLow,
    alignItems: 'center',
  },
  dietOptionActive: {
    borderColor: Paprika.brandSoft,
    backgroundColor: Paprika.brandTint,
  },
  dietOptionText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  dietOptionTextActive: {
    color: Paprika.primary,
  },
  removeBtn: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  removeBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    textDecorationLine: 'underline',
  },
});
